
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, Star, Zap, Crown, Loader2 } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '../config';

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: '₹0',
    original: null,
    features: ['1 Free Mock Total', 'Basic Analytics', 'Limited Leaderboard'],
    badge: 'Starter',
    color: 'bg-gray-100 text-gray-700',
    button: 'Current Plan',
    active: true
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '₹699',
    original: '₹999',
    features: ['8 mocks per subject/month', 'Full Leaderboard', 'Weak-topic Breakdown', 'AI Feedback'],
    badge: 'Popular',
    color: 'bg-blue-100 text-blue-700',
    button: 'Upgrade to Pro',
    active: false,
    icon: <Zap className="h-5 w-5" />
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '₹999',
    original: '₹1499',
    features: ['10 mocks per subject/month', 'Rank Prediction', 'Detailed Solutions', 'Compare with Toppers', 'Priority Support'],
    badge: 'Best Value',
    color: 'bg-amber-100 text-amber-700',
    button: 'Go Premium',
    active: false,
    icon: <Crown className="h-5 w-5" />
  }
];

export default function UpgradePage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(null);

  const handleUpgrade = async (planId) => {
    if (planId === 'free') return;
    setLoading(planId);

    try {
      // 1. Create Order
      const response = await axios.post(
        `${BACKEND_URL}/api/payment/create-order`,
        { plan: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { payment_session_id } = response.data.data;
        
        // 2. Load Cashfree SDK
        const cashfree = await loadCashfree();
        
        // 3. Start Checkout
        cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: "_self", // Redirects to return_url
        });
      }
    } catch (error) {
      console.error('Payment Error:', error);
      alert('Failed to initiate payment. Please try again.');
      setLoading(null);
    }
  };

  const loadCashfree = () => {
    return new Promise((resolve) => {
      if (window.Cashfree) {
        resolve(new window.Cashfree({ mode: "production" }));
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
      script.onload = () => {
        const cashfree = new window.Cashfree({
          mode: "production" // Always production for this user
        });
        resolve(cashfree);
      };
      document.body.appendChild(script);
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
            Upgrade Your Preparation
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            Get up to 10 mocks per subject per month and unlock detailed analytics to ace CUET.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative border-2 transition-all duration-300 ${
                plan.id === 'premium' ? 'border-amber-400 shadow-xl scale-105' : 'border-gray-200 hover:border-blue-300 hover:shadow-lg'
              }`}
            >
              {plan.id === 'premium' && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-sm">
                  Best Value
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <Badge className={`mx-auto mb-4 ${plan.color} border-0 px-3 py-1 text-sm`}>
                  {plan.id === 'premium' ? 'Top Rankers' : plan.badge}
                </Badge>
                <CardTitle className="text-2xl flex items-center justify-center gap-2">
                  {plan.icon} {plan.name}
                </CardTitle>
                <div className="mt-4">
                  {plan.original && (
                    <span className="text-gray-400 line-through text-lg mr-2">{plan.original}</span>
                  )}
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500 ml-1">/month</span>
                </div>
              </CardHeader>

              <CardContent className="pt-6">
                <ul className="space-y-4">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <div className="flex-shrink-0">
                        <Check className="h-5 w-5 text-green-500" />
                      </div>
                      <p className="ml-3 text-sm text-gray-700">{feature}</p>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button 
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={loading !== null || plan.id === 'free'}
                  className={`w-full py-6 text-lg font-semibold ${
                    plan.id === 'premium' 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white' 
                      : plan.id === 'pro'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-100 text-gray-400 hover:bg-gray-100 cursor-default'
                  }`}
                >
                  {loading === plan.id ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : null}
                  {plan.button}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            Secure payment via Cashfree. Plans valid for 30 days.
          </p>
          <p className="text-gray-400 text-xs mt-2">
            Any payment concerns? Email us at{' '}
            <a href="mailto:info@crackcuet.com" className="text-blue-500 hover:underline">
              info@crackcuet.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
